import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { ImpersonationService, ImpersonationContext } from '../../core/services/impersonation.service';
import { InboxService } from '../../core/services/inbox.service';
import { environment } from '../../../environments/environment';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  exact: boolean;
}

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  collapsed = false;
  pageTitle = 'Overview';
  userMenuOpen = false;
  bugReportOpen = false;
  notificationsOpen = false;
  exitingImpersonation = false;
  unreadCount = 0;

  readonly navItems: NavItem[] = [
    { label: 'Overview', route: '/',         icon: 'layout-dashboard', exact: true  },
    { label: 'Cloud',    route: '/cloud',    icon: 'cloud',             exact: false },
    { label: 'GitHub',   route: '/github',   icon: 'github',            exact: false },
    { label: 'Jira',     route: '/jira',     icon: 'list-todo',         exact: false },
    { label: 'Settings', route: '/settings', icon: 'settings',          exact: false },
  ];

  private readonly routeTitles: Record<string, string> = {
    '/':                           'Overview',
    '/github':                     'GitHub',
    '/jira':                       'Jira',
    '/cloud':                      'Cloud',
    '/settings':                   'Settings',
    '/settings/account':           'Account',
    '/settings/billing':           'Billing',
    '/settings/integrations':      'Integrations',
  };

  readonly currentUser$:  Observable<unknown>  = this.auth.currentUser$;
  readonly displayName$:  Observable<string>   = this.auth.displayName$;
  readonly userInitials$: Observable<string>   = this.auth.userInitials$;
  readonly profile$:      Observable<unknown>  = this.auth.profile$;
  readonly tierLabel$:    Observable<string>   = this.auth.tierLabel$;

  readonly isImpersonating$: Observable<boolean>                     = this.impersonation.isImpersonating$;
  readonly impersonationContext$: Observable<ImpersonationContext | null> = this.impersonation.context$;

  private routerSub?: Subscription;
  private inboxSub?: Subscription;
  private focusSub?: Subscription;

  constructor(
    private readonly auth:        AuthService,
    private readonly router:      Router,
    private readonly impersonation: ImpersonationService,
    private readonly inbox:       InboxService,
  ) {}

  ngOnInit(): void {
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(e => {
        const url = (e as NavigationEnd).urlAfterRedirects;
        // Service detail pages (/cloud/<service>) fall back to the section title.
        this.pageTitle = this.routeTitles[url] ?? (url.startsWith('/cloud/') ? 'Cloud' : 'Dashboard');
      });

    // Handle impersonation token from URL query param
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('impersonateToken');
    if (token && !this.impersonation.getSessionToken()) {
      this.impersonation.startFromToken(token).subscribe({
        next: () => {
          // Strip token from URL without a navigation event
          const url = new URL(window.location.href);
          url.searchParams.delete('impersonateToken');
          window.history.replaceState({}, '', url.toString());
        },
        error: () => {
          // Invalid/expired token — silently continue
        },
      });
    }

    // Sync unread count badge from inbox service.
    this.inboxSub = this.inbox.unreadCount$.subscribe(count => {
      this.unreadCount = count;
    });

    // Initial load and window-focus refresh.
    this.inbox.load().subscribe();
    this.focusSub = fromEvent(window, 'focus').subscribe(() => {
      this.inbox.load().subscribe();
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.inboxSub?.unsubscribe();
    this.focusSub?.unsubscribe();
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
  }

  openBugReport(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = false;
    this.bugReportOpen = true;
  }

  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  toggleNotifications(event: Event): void {
    event.stopPropagation();
    this.notificationsOpen = !this.notificationsOpen;
    if (this.userMenuOpen) this.userMenuOpen = false;
  }

  exitImpersonation(): void {
    if (this.exitingImpersonation) return;
    this.exitingImpersonation = true;
    this.impersonation.exit().subscribe({
      next: () => {
        this.exitingImpersonation = false;
      },
      error: () => {
        this.exitingImpersonation = false;
      },
    });
  }

  async signOut(): Promise<void> {
    this.userMenuOpen = false;
    await this.auth.signOut();
    window.location.href = environment.marketingUrl;
  }
}
